import { logger } from 'modules/logger';
import type { EnqueueToastFn } from 'common/ui/Toaster';
import type { SlateContent } from '../types';
import { findEnforcedEmptyRequiredFields } from './reportValidation';
import type { SubmissionTarget } from './useSubmissionWorkstationID';

/**
 * Shared submit-time guards/effects used by both the report submit path (useSubmitReport) and the
 * addendum submit path (useAddendumWorkflow.submitAddendum). Extracted from the handleSubmit-local
 * helpers so both owners can share them without cross-hook injection.
 */

/**
 * Returns true if submission should be blocked because required fields are empty (and opens the
 * empty-required-fields dialog). Prelim submissions skip this validation.
 */
export const enforceRequiredFields = ({
  content,
  isPrelim,
  isEmptyRequiredFieldsDialogOpen,
  openEmptyRequiredFieldsDialog,
  setAllRequiredFieldsTouched,
}: {
  content: SlateContent | null;
  isPrelim: boolean;
  isEmptyRequiredFieldsDialogOpen: boolean;
  openEmptyRequiredFieldsDialog: (
    emptyRequiredFields: ReturnType<typeof findEnforcedEmptyRequiredFields>
  ) => void;
  setAllRequiredFieldsTouched: () => void;
}): boolean => {
  if (!isEmptyRequiredFieldsDialogOpen && content) {
    const emptyRequiredFields = findEnforcedEmptyRequiredFields({ content, isPrelim });

    if (emptyRequiredFields.length > 0) {
      openEmptyRequiredFieldsDialog(emptyRequiredFields);
      setAllRequiredFieldsTouched();
      logger.info('[submitGuards] Attempted to submit with empty required fields', {
        count: emptyRequiredFields.length,
      });
      return true;
    }
  }
  return false;
};

export const notifySubmissionCancelled = ({
  enqueueToast,
  target,
  onError,
}: {
  enqueueToast: EnqueueToastFn;
  target: SubmissionTarget;
  onError?: () => void;
}): void => {
  const submissionLabel = target === 'addendum' ? 'Addendum' : 'Report';

  enqueueToast(`${submissionLabel} submission was cancelled.`, {
    icon: 'yellowWarning',
  });
  onError && onError();
};

export const notifyWorkstationPatchFailure = ({
  enqueueToast,
  target,
  onError,
}: {
  enqueueToast: EnqueueToastFn;
  target: SubmissionTarget;
  onError?: () => void;
}): void => {
  enqueueToast(
    `Could not update the workstation ID before submitting this ${target}. Please try again.`,
    {
      icon: 'redWarning',
    }
  );
  onError && onError();
};
